import { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Flag, ArrowRight, ShieldX, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner, EmptyState } from "@/components/shared";
import { ConfirmDialog } from "@/components/dialogs";

export default function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("open");
  const [dialog, setDialog] = useState(null); // {report, ban}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.reports();
      setReports(res.reports || []);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = reports.filter((r) => (tab === "open" ? !r.resolved : r.resolved));
  const openCount = reports.filter((r) => !r.resolved).length;

  const resolve = async (rid, ban) => {
    try {
      await api.resolveReport(rid, ban);
      toast.success(ban ? "Report resolved & user banned" : "Report dismissed");
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <PageHeader title="Reports" subtitle={`${openCount} open report${openCount === 1 ? "" : "s"}`} />

      <Tabs value={tab} onValueChange={setTab} className="mb-5">
        <TabsList data-testid="reports-tabs">
          <TabsTrigger value="open" data-testid="tab-open">Open ({openCount})</TabsTrigger>
          <TabsTrigger value="resolved" data-testid="tab-resolved">Resolved</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyState icon={Flag} title={tab === "open" ? "No open reports" : "No resolved reports"} hint="You're all caught up." />
      ) : (
        <div className="space-y-3" data-testid="reports-list">
          {filtered.map((r) => (
            <div
              key={r.report_id}
              className="rounded-2xl bg-card ring-1 ring-border/70 p-5"
              data-testid={`report-${r.report_id}`}
            >
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {/* Reporter */}
                  <div className="min-w-0">
                    <div className="text-xs text-muted-foreground">Reporter</div>
                    <div className="font-medium truncate">{r.reporter?.name || "—"}</div>
                    <div className="text-xs text-muted-foreground truncate">{r.reporter?.email}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                  {/* Reported */}
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Avatar src={r.reported?.photo} name={r.reported?.name} size={40} />
                    <div className="min-w-0">
                      <div className="text-xs text-muted-foreground">Reported</div>
                      <div className="font-medium truncate flex items-center gap-1.5">
                        {r.reported?.name || "—"}
                        {r.reported?.banned && <Tag tone="red">Banned</Tag>}
                        {r.reported?.verified && <Tag tone="green">Verified</Tag>}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{r.reported?.email}</div>
                    </div>
                  </div>
                </div>

                {!r.resolved && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => resolve(r.report_id, false)} data-testid={`dismiss-${r.report_id}`}>
                      <Check className="h-4 w-4" /> Dismiss
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDialog({ report: r })}
                      data-testid={`ban-${r.report_id}`}
                      disabled={r.reported?.banned}
                    >
                      <ShieldX className="h-4 w-4" /> Resolve & Ban
                    </Button>
                  </div>
                )}
                {r.resolved && <Tag tone="green">Resolved</Tag>}
              </div>

              <div className="mt-3 rounded-lg bg-secondary/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">Reason: </span>
                {r.reason || "No reason provided"}
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!dialog}
        onOpenChange={(v) => !v && setDialog(null)}
        title={`Ban ${dialog?.report?.reported?.name || "user"}?`}
        description="This resolves the report and permanently bans the reported user."
        confirmText="Resolve & Ban"
        destructive
        onConfirm={() => resolve(dialog.report.report_id, true)}
      />
    </div>
  );
}
